import { COVERAGE_META } from "./SeverityBadge";

export const CoverageMatrix = ({ coverage }) => {
  const ovu = coverage.filter((c) => (c.matrix || "OVU") === "OVU");
  const kk = coverage.filter((c) => c.matrix === "KK");
  const areas = [...new Set(ovu.map((c) => c.area_name))];

  const stats = (rows) => {
    const st = { total: rows.length, covered: 0, partial: 0, not_found: 0 };
    rows.forEach((r) => {
      if (r.coverage === "EXPLICIT" || r.coverage === "IMPLICIT") st.covered += 1;
      else if (r.coverage === "PARTIAL") st.partial += 1;
      else if (r.coverage === "NOT_FOUND") st.not_found += 1;
    });
    st.pct = st.total ? Math.round(((st.covered + st.partial * 0.5) / st.total) * 100) : 0;
    return st;
  };

  const PctChip = ({ st }) => (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${st.pct >= 70 ? "bg-emerald-100 text-emerald-800" : st.pct >= 35 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
      {st.pct} %
    </span>
  );

  const Table = ({ rows, showSubarea }) => (
    <div className="overflow-x-auto overflow-hidden rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="coverage-matrix-table">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2.5 font-semibold">Kód</th>
            {showSubarea && <th className="px-4 py-2.5 font-semibold">Podoblast</th>}
            <th className="px-4 py-2.5 font-semibold">Prvek / OVU</th>
            <th className="px-4 py-2.5 font-semibold">Pokrytí</th>
            <th className="px-4 py-2.5 font-semibold">Umístění</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((c) => {
            const m = COVERAGE_META[c.coverage] || COVERAGE_META.UNVERIFIABLE;
            return (
              <tr key={c.code} data-testid={`coverage-row-${c.code}`} className="transition-colors hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{c.code}</td>
                {showSubarea && <td className="px-4 py-3 text-xs text-slate-500">{c.subarea}</td>}
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{c.element_name}</div>
                  {c.explanation && <div className="mt-0.5 text-xs text-slate-500 max-w-md">{c.explanation}</div>}
                  {c.quote && <div className="mt-1 text-xs italic text-slate-400 max-w-md">„{c.quote.slice(0, 140)}…“</div>}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>
                </td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.page ? `str. ${c.page}` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-8">
      {areas.map((area) => {
        const rows = ovu.filter((c) => c.area_name === area);
        const st = stats(rows);
        const subs = [...new Set(rows.map((r) => r.subarea))].filter(Boolean);
        return (
          <div key={area}>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">{area}</h3>
              <PctChip st={st} />
              <span className="text-xs text-slate-400">doloženo {st.covered}, částečně {st.partial}, nenalezeno {st.not_found} z {st.total}</span>
            </div>
            {subs.length > 1 && (
              <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`subarea-summary-${area}`}>
                {subs.map((sub) => {
                  const sst = stats(rows.filter((r) => r.subarea === sub));
                  return (
                    <span key={sub} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-600">
                      {sub} <PctChip st={sst} />
                    </span>
                  );
                })}
              </div>
            )}
            <Table rows={rows} showSubarea />
          </div>
        );
      })}
      {kk.length > 0 && (
        <div data-testid="kk-coverage-section">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Klíčové kompetence</h3>
            <PctChip st={stats(kk)} />
          </div>
          <p className="mb-2 text-xs text-slate-400">
            V novém RVP PV se ke klíčovým kompetencím nepřiřazují jednotlivé OVU — pokrytí se hodnotí na úrovni celé kompetence.
          </p>
          <Table rows={kk} showSubarea={false} />
        </div>
      )}
      <p className="text-xs text-slate-400">
        „Nenalezeno“ znamená, že prvek nebyl v dokumentu doložen — nikoli že jej škola nerealizuje.
      </p>
    </div>
  );
};
